"use client";
import { PlayGameIcon } from "@/components/icons/PlayGameIcon";
import { Action, useAnalytics } from "@/context/AnalyticsProvider";
import { cn } from "@/lib/utils";
import { Button } from "@components/ui/button";
import { Card, CardContent } from "@components/ui/card";
import Image from "next/image";
import { useRouter } from "next/navigation";

export interface GameCardData {
  id: string;
  name: string;
  subtitle: string;
  description: string;
  icon: string;
  backgroundImage: string;
  url: string;
  slug?: string;
}

interface GameCardProps {
  game: GameCardData;
}

const BackgroundImage = ({
  backgroundImage,
  name,
}: { backgroundImage: string; name: string }) => {
  return (
    <div className="absolute inset-0 z-0">
      <Image src={backgroundImage} alt={name} fill className="object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/70 shadow-[inset_0_0_100px_50px_rgba(0,0,0,0.3)] bg-black/40" />
    </div>
  );
};

const GameIcon = ({
  icon,
  name,
  className,
}: { icon: string; name: string; className?: string }) => {
  return (
    <div
      className={cn(
        "w-12 h-12 bg-black/50 rounded border-2 border-white/20 flex items-center justify-center",
        className,
      )}
    >
      <Image
        src={icon}
        alt={name}
        width={48}
        height={48}
        className="object-contain"
      />
    </div>
  );
};

const StatusBadge = () => {
  return (
    <div className="flex items-center gap-2">
      <div className="w-6 h-6 relative bg-[#25F352] rounded flex items-center justify-center">
        <p className="text-white text-xs font-bold">M</p>
      </div>
      <p className="text-[0.625rem] font-normal text-white/70 tracking-[0.125rem] uppercase">
        ON MARBLEX
      </p>
    </div>
  );
};

const GameInfo = ({
  name,
  subtitle,
  description,
  className,
}: {
  name: string;
  subtitle: string;
  description: string;
  className?: string;
}) => {
  return (
    <div className={cn("space-y-1", className)}>
      <StatusBadge />

      <h3 className="text-[1.75rem] font-bold text-white leading-[2.25rem]">
        {name}
      </h3>
      <h4 className="text-[1.75rem] font-bold text-white leading-[2.25rem] whitespace-pre-line">
        {subtitle}
      </h4>

      <p className="text-sm text-white/80 leading-5">{description}</p>
    </div>
  );
};

const JoinNowButton = ({
  name,
  gameSlug,
  url,
  className,
}: { name: string; gameSlug?: string; url: string; className?: string }) => {
  const router = useRouter();
  const { trackV3 } = useAnalytics();
  const control = gameSlug ? "JoinNowButton" : "WatchNowButton";

  const handleClick = () => {
    trackV3({
      object: "Button",
      action: Action.PRESSED,
      extras: {
        control,
        name,
      },
    });
    if (gameSlug) {
      router.push(`/games/${gameSlug}/`);
    } else {
      window.open(url, "_blank");
    }
  };
  return (
    <Button
      className={cn(
        "w-48 h-12 pl-4 bg-black border-2 border-[#25F352] rounded-3xl hover:bg-[#25F352]/80",
        className,
      )}
      onClick={handleClick}
    >
      <div className="flex items-center gap-2">
        <PlayGameIcon className="w-6 h-6" />
        <p className="text-base font-semibold text-white">
          {gameSlug ? "Join Now" : "Watch Now"}
        </p>
      </div>
    </Button>
  );
};

export const GameCard = ({ game }: GameCardProps) => {
  return (
    <Card className="min-w-[24.5625rem] max-w-[24.5625rem] min-h-[32.5rem] max-h-[32.5rem] relative overflow-hidden bg-[#202020] border-0 flex-shrink-0">
      <BackgroundImage
        backgroundImage={game.backgroundImage}
        name={game.name}
      />

      <CardContent className="absolute inset-0 z-10 p-6 grid grid-rows-3 gap-6">
        <GameIcon icon={game.icon} name={game.name} className="self-start" />

        <GameInfo
          name={game.name}
          subtitle={game.subtitle}
          description={game.description}
          className="self-center"
        />

        <JoinNowButton
          gameSlug={game.slug}
          name={game.name}
          url={game.url}
          className="self-end"
        />
      </CardContent>
    </Card>
  );
};
